// Themed dropdown pickers for the client-portal case editor.
//
// Why these exist: free-text inputs for "eligible schools" and "eligible
// majors" let clients fat-finger names that don't match what students
// type into their profile. Constraining to a known list (sourced from
// `allowed_email_domains` for schools, the curated `MAJORS` list for
// majors) makes the eligibility filter actually work.
//
// We render our own modal instead of using <Modal> from components/ui
// because that one wraps children in an outer <ScrollView> + <Pressable>
// that interfered with the search TextInput on web. This file owns the
// full modal markup so the search input is just sitting in a normal View
// and behaves like any other input.

import { useState } from "react";
import {
  Modal as RNModal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from "react-native";
import { FONTS } from "../../lib/brand";
import { PANE_THEMES } from "../brand/paneThemes";

const THEME = PANE_THEMES.Client;
const AMBER = THEME.accent;
const OFF_BLACK = THEME.surfaceBg;
const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";
const BLACK_SURFACE = "rgba(0,0,0,0.4)";

const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};

// ---------------------------------------------------------------------------
// PickerTrigger
// ---------------------------------------------------------------------------

function PickerTrigger({
  onPress,
  disabled,
  children,
}: {
  onPress: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      style={[
        {
          marginTop: 8,
          backgroundColor: BLACK_SURFACE,
          borderWidth: 1,
          borderColor: BORDER,
          borderRadius: 3,
          paddingHorizontal: 14,
          paddingVertical: 12,
          minHeight: 44,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          opacity: disabled ? 0.7 : 1,
        },
        !disabled ? cursorWeb : null,
      ]}
    >
      <View style={{ flex: 1 }}>{children}</View>
      <Text style={{ color: WL, fontFamily: FONTS.body, fontSize: 14 }}>▾</Text>
    </Pressable>
  );
}

// ---------------------------------------------------------------------------
// PickerModal — self-contained dark modal.
// ---------------------------------------------------------------------------

function PickerModal({
  visible,
  onClose,
  title,
  children,
  maxWidth = 460,
}: {
  visible: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: number;
}) {
  return (
    <RNModal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        onPress={onClose}
        style={[
          {
            flex: 1,
            backgroundColor: "rgba(0,0,0,0.85)",
            alignItems: "center",
            justifyContent: "center",
            paddingHorizontal: 16,
          },
        ]}
      >
        <Pressable
          onPress={() => {}}
          style={[
            {
              width: "100%",
              maxWidth,
              backgroundColor: OFF_BLACK,
              borderRadius: 6,
              borderWidth: 1,
              borderColor: "rgba(181,135,58,0.2)",
            },
            ]}
        >
          <View
            style={{
              borderBottomWidth: 1,
              borderBottomColor: "rgba(181,135,58,0.15)",
              paddingHorizontal: 22,
              paddingVertical: 16,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Text
              style={{
                color: W,
                fontFamily: FONTS.display,
                fontSize: 20,
              }}
            >
              {title}
            </Text>
            <Pressable
              onPress={onClose}
              style={[
                {
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  borderWidth: 1,
                  borderColor: "rgba(255,255,255,0.25)",
                  backgroundColor: "rgba(255,255,255,0.06)",
                  alignItems: "center",
                  justifyContent: "center",
                },
                cursorWeb,
              ]}
            >
              <Text style={{ color: WS, fontSize: 14, lineHeight: 14 }}>×</Text>
            </Pressable>
          </View>
          <View style={{ padding: 18 }}>{children}</View>
        </Pressable>
      </Pressable>
    </RNModal>
  );
}

// ---------------------------------------------------------------------------
// MultiSelectField
// ---------------------------------------------------------------------------

export function MultiSelectField({
  values,
  onChange,
  options,
  editable = true,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  modalTitle = "Select",
  emptyHint,
}: {
  values: string[];
  onChange: (v: string[]) => void;
  options: ReadonlyArray<string>;
  editable?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  modalTitle?: string;
  emptyHint?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? options.filter((o) => o.toLowerCase().includes(needle))
    : options.slice();

  function toggle(opt: string) {
    if (values.includes(opt)) onChange(values.filter((v) => v !== opt));
    else onChange([...values, opt]);
  }

  function handleClose() {
    setOpen(false);
    setQ("");
  }

  return (
    <>
      <PickerTrigger onPress={() => setOpen(true)} disabled={!editable}>
        {values.length === 0 ? (
          <Text style={{ color: WL, fontFamily: FONTS.body, fontSize: 13.5 }}>
            {placeholder}
          </Text>
        ) : (
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
            {values.map((v) => (
              <View
                key={v}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  paddingHorizontal: 10,
                  paddingVertical: 4,
                  borderRadius: 999,
                  borderWidth: 1,
                  borderColor: "rgba(181,135,58,0.4)",
                  backgroundColor: "rgba(181,135,58,0.08)",
                  gap: 7,
                }}
              >
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: AMBER }}>
                  {v}
                </Text>
                {editable ? (
                  <Pressable
                    onPress={(e) => {
                      e.stopPropagation?.();
                      onChange(values.filter((x) => x !== v));
                    }}
                    style={cursorWeb}
                    hitSlop={6}
                  >
                    <Text
                      style={{
                        fontFamily: FONTS.bodyBold,
                        fontSize: 12,
                        color: AMBER,
                        lineHeight: 12,
                      }}
                    >
                      ×
                    </Text>
                  </Pressable>
                ) : null}
              </View>
            ))}
          </View>
        )}
      </PickerTrigger>

      <PickerModal visible={open} onClose={handleClose} title={modalTitle}>
        {options.length === 0 && emptyHint ? (
          <Text
            style={{
              color: WM,
              fontFamily: FONTS.body,
              fontSize: 13,
              paddingVertical: 12,
            }}
          >
            {emptyHint}
          </Text>
        ) : (
          <>
            <TextInput
              value={q}
              onChangeText={setQ}
              placeholder={searchPlaceholder}
              placeholderTextColor={WL}
              autoFocus
              autoCorrect={false}
              autoCapitalize="none"
              style={{
                backgroundColor: BLACK_SURFACE,
                borderWidth: 1,
                borderColor: BORDER,
                borderRadius: 3,
                paddingHorizontal: 12,
                paddingVertical: 10,
                color: W,
                fontFamily: FONTS.body,
                fontSize: 13.5,
                marginBottom: 6,
              }}
            />
            <Text
              style={{
                color: WL,
                fontFamily: FONTS.body,
                fontSize: 10.5,
                marginBottom: 8,
                paddingHorizontal: 2,
              }}
            >
              {filtered.length} of {options.length}
              {values.length > 0 ? ` Â· ${values.length} selected` : ""}
            </Text>
            <ScrollView style={{ maxHeight: 320 }} keyboardShouldPersistTaps="handled">
              {filtered.length === 0 ? (
                <Text
                  style={{
                    color: WM,
                    fontFamily: FONTS.body,
                    fontSize: 13,
                    paddingVertical: 12,
                    textAlign: "center",
                  }}
                >
                  No matches.
                </Text>
              ) : (
                filtered.map((opt) => {
                  const selected = values.includes(opt);
                  return (
                    <Pressable
                      key={opt}
                      onPress={() => toggle(opt)}
                      style={[
                        {
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 10,
                          paddingHorizontal: 6,
                          paddingVertical: 9,
                          borderRadius: 3,
                        },
                        cursorWeb,
                      ]}
                    >
                      <View
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 3,
                          borderWidth: 1,
                          borderColor: selected ? AMBER : "rgba(255,255,255,0.25)",
                          backgroundColor: selected ? "rgba(181,135,58,0.18)" : "transparent",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {selected ? (
                          <Text
                            style={{
                              color: AMBER,
                              fontSize: 11,
                              lineHeight: 12,
                              fontFamily: FONTS.bodyBold,
                            }}
                          >
                            ✓
                          </Text>
                        ) : null}
                      </View>
                      <Text
                        style={{
                          color: selected ? W : WS,
                          fontFamily: FONTS.body,
                          fontSize: 13.5,
                        }}
                      >
                        {opt}
                      </Text>
                    </Pressable>
                  );
                })
              )}
            </ScrollView>
          </>
        )}

        <View
          style={{
            marginTop: 14,
            paddingTop: 12,
            borderTopWidth: 1,
            borderTopColor: BORDER,
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Pressable
            onPress={() => onChange([])}
            disabled={values.length === 0}
            style={[cursorWeb, { opacity: values.length === 0 ? 0.3 : 1 }]}
          >
            <Text
              style={{
                color: WM,
                fontFamily: FONTS.bodyBold,
                fontSize: 10,
                letterSpacing: 1.4,
                textTransform: "uppercase",
              }}
            >
              Clear all
            </Text>
          </Pressable>
          <Pressable
            onPress={handleClose}
            style={[
              {
                paddingHorizontal: 18,
                paddingVertical: 9,
                borderRadius: 3,
                borderWidth: 1,
                borderColor: "rgba(181,135,58,0.5)",
                backgroundColor: "rgba(181,135,58,0.12)",
              },
              cursorWeb,
            ]}
          >
            <Text
              style={{
                color: AMBER,
                fontFamily: FONTS.bodyBold,
                fontSize: 10,
                letterSpacing: 1.4,
                textTransform: "uppercase",
              }}
            >
              Done
            </Text>
          </Pressable>
        </View>
      </PickerModal>
    </>
  );
}

// ---------------------------------------------------------------------------
// SelectField — single-select. Used for grad year min / max.
// ---------------------------------------------------------------------------

export function SelectField({
  value,
  onChange,
  options,
  editable = true,
  placeholder = "Select…",
  modalTitle = "Select",
  formatOption,
  allowClear = true,
}: {
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<string | number>;
  editable?: boolean;
  placeholder?: string;
  modalTitle?: string;
  formatOption?: (v: string | number) => string;
  allowClear?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const fmt = (v: string | number) => (formatOption ? formatOption(v) : String(v));

  return (
    <>
      <PickerTrigger onPress={() => setOpen(true)} disabled={!editable}>
        <Text
          style={{
            color: value ? W : WL,
            fontFamily: FONTS.body,
            fontSize: 13.5,
          }}
        >
          {value ? fmt(value) : placeholder}
        </Text>
      </PickerTrigger>

      <PickerModal visible={open} onClose={() => setOpen(false)} title={modalTitle} maxWidth={360}>
        <ScrollView style={{ maxHeight: 360 }}>
          {options.map((opt) => {
            const optStr = String(opt);
            const selected = optStr === value;
            return (
              <Pressable
                key={optStr}
                onPress={() => {
                  onChange(optStr);
                  setOpen(false);
                }}
                style={[
                  {
                    paddingHorizontal: 8,
                    paddingVertical: 11,
                    borderRadius: 3,
                    backgroundColor: selected ? "rgba(181,135,58,0.08)" : "transparent",
                  },
                  cursorWeb,
                ]}
              >
                <Text
                  style={{
                    color: selected ? AMBER : WS,
                    fontFamily: selected ? FONTS.bodyBold : FONTS.body,
                    fontSize: 14,
                  }}
                >
                  {fmt(opt)}
                </Text>
              </Pressable>
            );
          })}
        </ScrollView>

        {allowClear ? (
          <View
            style={{
              marginTop: 14,
              paddingTop: 12,
              borderTopWidth: 1,
              borderTopColor: BORDER,
              alignItems: "flex-start",
            }}
          >
            <Pressable
              onPress={() => {
                onChange("");
                setOpen(false);
              }}
              disabled={!value}
              style={[cursorWeb, { opacity: value ? 1 : 0.3 }]}
            >
              <Text
                style={{
                  color: WM,
                  fontFamily: FONTS.bodyBold,
                  fontSize: 10,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                }}
              >
                Clear
              </Text>
            </Pressable>
          </View>
        ) : null}
      </PickerModal>
    </>
  );
}
